import { useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { useDismiss } from '@/hooks/useDismiss'
import { cn } from '@/utils/cn'
import { SheetHandle, SheetScrim, sheetDocked, sheetSafeArea } from '@/components/ui/Sheet'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { useIsPhone } from '@/hooks/useIsPhone'
import { SheetPortal } from '@/components/ui/Sheet'

interface DropdownProps {
  trigger: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  align?: 'left' | 'right'
  children: ReactNode
}

export function Dropdown({ trigger, open, onOpenChange, align = 'left', children }: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const isPhone = useIsPhone()
  const dismiss = useCallback(() => onOpenChange(false), [onOpenChange])

  useDismiss(ref, dismiss, open, { alsoInside: menuRef })
  useSheetAnchor(open, triggerRef, menuRef)

  return (
    <div ref={ref} className="relative">
      <div ref={triggerRef}>{trigger}</div>
      {open && (
        isPhone ? (
          <SheetPortal>
            <SheetScrim onClick={dismiss} />
            <div ref={menuRef} data-testid="bottom-sheet" className={cn(sheetDocked, sheetSafeArea)}>
              <SheetHandle />
              {children}
            </div>
          </SheetPortal>
        ) : (
          <div
            ref={menuRef}
            className={cn(
              'absolute top-full mt-2 z-50 min-w-[200px] max-w-[calc(100vw-2rem)]',
              'rounded-lg border border-border-divider bg-card shadow-lg',
              align === 'right' ? 'right-0' : 'left-0',
            )}
          >
            {children}
          </div>
        )
      )}
    </div>
  )
}
